/**
 * @fileoverview Resolve a prompt group UUID to a numeric prompt ID.
 * @module @mutagent/cli/lib/resolve-prompt-id
 *
 * Several API resources (datasets, evaluations, optimization jobs)
 * reference their owning prompt by `promptGroupId` (a UUID for the
 * prompt family). The MutagenT UI v2, however, routes prompts by
 * their numeric `id` (the latest version of the family):
 *
 *   /prompts/:id          → parseInt(id) → numeric prompt id
 *
 * This helper resolves a `promptGroupId` to the numeric `id` of the
 * latest prompt in that group, so CLI deep links open the right page.
 */
import type { Prompt } from '../types/api.js';

/**
 * Minimal client surface required by `resolveNumericPromptId`.
 * Defined locally to avoid coupling this helper to `SDKClientWrapper`,
 * which makes the helper trivially testable with a stub.
 */
export interface PromptListClient {
  listPrompts(): Promise<Prompt[]>;
}

/**
 * Resolve a prompt group UUID to the numeric `id` of the latest
 * prompt version in the group.
 *
 * Selection rule:
 *   1. Among prompts matching `promptGroupId`, prefer the one flagged `isLatest`.
 *   2. Otherwise, pick the most recently updated (by `updatedAt`).
 *   3. If no prompts exist for the group, return `null` (orphaned resource).
 *
 * Never throws — callers must handle `null` and fall back gracefully
 * (e.g. to a generic dashboard link).
 *
 * @param client - Anything with a `listPrompts()` method (real SDK or stub).
 * @param promptGroupId - The UUID of the prompt family.
 * @returns The numeric prompt id, or `null` if no prompts in the group.
 */
export async function resolveNumericPromptId(
  client: PromptListClient,
  promptGroupId: string,
): Promise<number | null> {
  if (!promptGroupId) return null;

  let prompts: Prompt[];
  try {
    prompts = await client.listPrompts();
  } catch {
    // Best-effort: never block link generation on a list failure.
    return null;
  }

  const matches = prompts.filter((p) => p.promptGroupId === promptGroupId);
  if (matches.length === 0) return null;

  const latestFlagged = matches.find((p) => p.isLatest);
  if (latestFlagged) return latestFlagged.id;

  // Fallback: most recently updated
  const sorted = [...matches].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });
  return sorted[0]?.id ?? null;
}
